import React from 'react';

import { useBillingInformationQuery } from '@edx/graphql/generated';
import { useAppConfigStateContext } from '@edx/contexts/app-config';
import { Body, Intro } from '@edx/components/typography';

export const PricingFAQ: React.FC = () => {
  const { projectShortName } = useAppConfigStateContext();
  const { data } = useBillingInformationQuery();
  const [monthlyPlan, quarterlyPlan, yearlyPlan] = data?.billingInformation.plans || [];

  return (
    <div className="grid grid-cols-12">
      <section className="col-start-1 lg:col-start-2 col-span-full lg:col-span-10 mx-0 font-body">
        <Intro tag="h2" className="text-gray-300 text-center">
          Preguntas frecuentes
        </Intro>

        <div className="flex flex-wrap mt-12">
          <div className="w-full md:w-1/2 p-4">
            <Intro tag="h3" size="small" className="text-gray-300 mb-6">
              Acerca de {projectShortName}
            </Intro>

            <article className="text-gray-500 border-t border-gray-600 border-opacity-50 px-2 pt-2 pb-6">
              <Body tag="h4" className="text-gray-300">
                ¿Qué es {projectShortName}?
              </Body>

              <div className="mt-2 space-y-3">
                <Body>{projectShortName} es una escuela online donde aprender a tocar el bajo eléctrico.</Body>
                <Body>
                  Mediante nuestros cursos con lecciones prácticas e interactivas, disfrutarás aprendiendo de la mano de
                  los mejores profesionales.
                </Body>
                <Body>
                  En la comunidad {projectShortName} además, podrás resolver tus dudas y podrás compartir tus dudas y
                  experiencias con otros músicos y aficionados.
                </Body>
              </div>
            </article>

            <article className="text-gray-500 border-t border-gray-600 border-opacity-50 px-2 pt-2 pb-6">
              <Body tag="h4" className="text-gray-300">
                ¿Cómo puedo acceder a {projectShortName}?
              </Body>

              <div className="mt-2 space-y-3">
                <Body>
                  {projectShortName} está optimizada para que puedas acceder cómodamente desde cualquier sitio (con
                  conexión a internet) a través de tu dispositivo favorito: móviles, tablets, laptops o ordenadores de
                  sobremesa.
                </Body>
              </div>
            </article>

            <article className="text-gray-500 border-t border-gray-600 border-opacity-50 px-2 pt-2 pb-6">
              <Body tag="h4" className="text-gray-300">
                ¿A quién va dirigido {projectShortName}?
              </Body>

              <div className="mt-2 space-y-3">
                <Body>
                  En {projectShortName} ofrecemos contenidos para todos los niveles. Tanto si no has tocado nunca, como
                  si quieres mejorar tu técnica, en {projectShortName} encontrarás contenidos que encajen con tus
                  necesidades.
                </Body>
              </div>
            </article>
          </div>
          <div className="w-full md:w-1/2 p-4">
            <Intro tag="h3" size="small" className="text-gray-300 mb-6">
              Suscripción
            </Intro>

            <article className="text-gray-500 border-t border-gray-600 border-opacity-50 px-2 pt-2 pb-6">
              <Body tag="h4" className="text-gray-300">
                ¿Qué ventajas ofrece una suscripción?
              </Body>

              <div className="mt-2 space-y-3">
                <Body>Con tu suscripción de pago, podrás disfrutar de:</Body>
                <ul className="space-y-2">
                  <li>- Acceso ilimitado a todos los cursos</li>
                  <li>- Acceso ilimitado a todas las canciones</li>
                  <li>- Acceso ilimitado a todas las descargas de partituras y audios</li>
                  <li>- Seguimiento del progreso</li>
                </ul>
              </div>
            </article>

            <article className="text-gray-500 border-t border-gray-600 border-opacity-50 px-2 pt-2 pb-6">
              <Body tag="h4" className="text-gray-300">
                ¿Cómo puedo activar mi suscripción?
              </Body>

              <div className="mt-2 space-y-3">
                <Body>
                  Para activar tu suscripción sólo tienes que seleccionar tu modalidad de pago (Mensual, Trimestral,
                  Anual) y realizar el primer pago con una tarjeta de crédito válida.
                </Body>
              </div>
            </article>

            <article className="text-gray-500 border-t border-gray-600 border-opacity-50 px-2 pt-2 pb-6">
              <Body tag="h4" className="text-gray-300">
                ¿Cómo puedo dar de baja mi suscripción?
              </Body>

              <div className="mt-2 space-y-3">
                <Body>
                  Podrás cancelar tu suscripción en cualquier momento a través del apartado Suscripción dentro de
                  Ajustes. Una vez hayas cancelada tu suscripción, seguirás teniendo acceso a todos los contenidos hasta
                  el final del ciclo de suscripción (1 mes, 3 meses o 12 meses).
                </Body>
              </div>
            </article>

            <article className="text-gray-500 border-t border-gray-600 border-opacity-50 px-2 pt-2 pb-6">
              <Body tag="h4" className="text-gray-300">
                ¿Cómo puedo cambiar los datos de mi suscripción?
              </Body>

              <div className="mt-2 space-y-3">
                <Body>
                  Si ya tienes una suscripción activa, podrás actualizar los datos de tu tarjeta de crédito a través del
                  apartado Tarjeta de crédito (dentro de Ajustes), y el tipo de suscripción desde Suscripción (en
                  Ajustes).
                </Body>
              </div>
            </article>
          </div>
          <div className="w-full md:w-1/2 p-4">
            <Intro tag="h3" size="small" className="text-gray-300 mb-6">
              Facturación
            </Intro>

            <article className="text-gray-500 border-t border-gray-600 border-opacity-50 px-2 pt-2 pb-6">
              <Body tag="h4" className="text-gray-300">
                ¿Cómo se realizan los cobros?
              </Body>

              <div className="mt-2 space-y-3">
                <Body>
                  El primer cobro se realizará inmediatamente después de activar la suscripción. Los siguientes cobros
                  se realizarán en función de la modalidad de suscripción seleccionada:
                </Body>
                <ul className="space-y-2">
                  <li>- Mensual: se realizará un cobro de {monthlyPlan?.priceFormatted} cada mes.</li>
                  <li>- Trimestral: se realizará un cobro de {quarterlyPlan?.priceFormatted} cada 3 meses.</li>
                  <li>- Anual: se realizará un cobro de {yearlyPlan?.priceFormatted} cada año.</li>
                </ul>
              </div>
            </article>

            <article className="text-gray-500 border-t border-gray-600 border-opacity-50 px-2 pt-2 pb-6">
              <Body tag="h4" className="text-gray-300">
                ¿Qué tipo de tarjeta necesito para realizar el pago?
              </Body>

              <div className="mt-2 space-y-3">
                <Body>Puedes realizar los pagos con cualquier tarjeta de crédito o débito, válida y activa.</Body>
                <Body>Aceptamos Visa, Mastercard, American Express, Discover, Diners Club, JC, etc.</Body>
              </div>
            </article>

            <article className="text-gray-500 border-t border-gray-600 border-opacity-50 px-2 pt-2 pb-6">
              <Body tag="h4" className="text-gray-300">
                ¿Porqué ha fallado el pago de mi suscripción?
              </Body>

              <div className="mt-2 space-y-3">
                <Body>
                  Hay diferentes casos por los que el pago de la suscripción puede fallar. Los casos más comunes son:
                </Body>
              </div>
            </article>

            <article className="text-gray-500 border-t border-gray-600 border-opacity-50 px-2 pt-2 pb-6">
              <Body tag="h4" className="text-gray-300">
                ¿Cómo puedo cambiar los datos de mi suscripción?
              </Body>

              <div className="mt-2 space-y-3">
                <Body>
                  Si ya tienes una suscripción activa, podrás actualizar los datos de tu tarjeta de crédito a través del
                  apartado Tarjeta de crédito (dentro de Ajustes), y el tipo de suscripción desde Suscripción (en
                  Ajustes).
                </Body>
                <ul className="space-y-2">
                  <li>- Datos introducidos erróneos.</li>
                  <li>- Tarjeta de crédito expirada.</li>
                  <li>- La cuenta corriente no ofrece suficientes fondos.</li>
                  <li>- La transacción tiene un alto riesgo de fraude.</li>
                  <li>- El banco rechaza por otras razones.</li>
                </ul>
              </div>
            </article>
          </div>
        </div>
      </section>
    </div>
  );
};
